/**
 * Team Uploads API Service
 * Handles uploading and listing files (e.g. schedules) attached to a team.
 * Files live in GCS; upload uses a signed-URL handshake:
 *   request-url -> PUT raw file to GCS -> complete.
 */

import { get, post, del } from "./api";
import type {
  TeamUploadUrlRequest,
  TeamUploadUrlResponse,
  TeamUploadResponse,
} from "../types/api";

/** Request a signed URL for uploading a file to the given team. */
export const requestTeamUploadUrl = async (
  teamId: string,
  req: TeamUploadUrlRequest
): Promise<TeamUploadUrlResponse> => {
  return post<TeamUploadUrlResponse>(
    `/api/teams/${teamId}/uploads/upload-url`,
    req
  );
};

/**
 * PUT the raw file to the GCS signed URL. Uses a bare `fetch` (NOT the authed
 * api client) so the Firebase Bearer token is never sent to the signed URL.
 *
 * The `Content-Type` header is REQUIRED here and must equal the `contentType`
 * sent to `requestTeamUploadUrl`: the backend signs the URL with Content-Type
 * as a signed header (`WithContentHeaders`), so GCS 403s if the PUT's
 * Content-Type doesn't match the signed value. (Do not "align" this with
 * services/uploads.ts, which omits the header — that flow only ever uploads
 * images whose browser-detected type happens to match; team uploads accept
 * PDF/XLSX/ICS/etc. where the browser's auto type would not match.)
 */
export const uploadTeamFileToGCS = async (
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> => {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`GCS upload failed: ${response.statusText}`);
    }
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
      throw new Error(
        "Upload failed due to network error. This may be a CORS configuration issue. " +
          "Please try again or contact support if the problem persists."
      );
    }
    throw error;
  }
};

/** Finalize an upload after the file has been PUT to GCS. */
export const completeTeamUpload = async (
  teamId: string,
  uploadId: string
): Promise<TeamUploadResponse> => {
  return post<TeamUploadResponse>(
    `/api/teams/${teamId}/uploads/${uploadId}/complete`,
    undefined
  );
};

/** List a team's uploads (newest first; each has a fresh signed download URL). */
export const getTeamUploads = async (
  teamId: string
): Promise<TeamUploadResponse[]> => {
  return get<TeamUploadResponse[]>(`/api/teams/${teamId}/uploads`);
};

/** Delete a team upload. */
export const deleteTeamUpload = async (
  teamId: string,
  uploadId: string
): Promise<void> => {
  return del<void>(`/api/teams/${teamId}/uploads/${uploadId}`);
};

/**
 * Orchestrate a full team file upload: request signed URL, PUT the file to GCS,
 * then complete. Returns the finalized upload record.
 */
export const uploadTeamFile = async (
  teamId: string,
  file: File
): Promise<TeamUploadResponse> => {
  const dotIndex = file.name.lastIndexOf(".");
  const fileExtension =
    dotIndex > -1 ? file.name.slice(dotIndex).toLowerCase() : "";
  const contentType = file.type || "application/octet-stream";

  const { uploadId, uploadUrl } = await requestTeamUploadUrl(teamId, {
    fileName: file.name,
    fileExtension,
    contentType,
    fileSizeBytes: file.size,
  });

  await uploadTeamFileToGCS(uploadUrl, file, contentType);

  return completeTeamUpload(teamId, uploadId);
};
